'use strict';

// Vue
import Vue from 'vue';

// Config
import config from 'config';

// Packages
import { createClient } from 'contentful';

// Services
import { formatCategories, formatArticlesPerTopic, formatTopics, formatArticle } from 'utils/contentful';

const state = {
  client: null,
  entries: {},
};

const getters = {
  client: state => state.client,
  loading: state => state.loading,
  loadingSearch: state => state.loadingSearch,
  categories: state => formatCategories(state.categories),
  topics: state => formatTopics(state.topics),
  topic: state => (id) => _.find(formatTopics(state.topics), topic => topic.id === id), // eslint-disable-line no-undef
  articles: state => formatArticlesPerTopic(state.articles),
  highlightedArticles: state => formatArticlesPerTopic(state.highlightedArticles),
  article: state => state.article ? formatArticle(state.article) : {},
};

const mutations = {
  SET_LOADING_STATE (state, payload) {
    Vue.set(state, 'loading', payload);
  },

  SET_LOADING_SEARCH_STATE (state, payload) {
    setTimeout(() => {
      Vue.set(state, 'loadingSearch', payload);
    }, payload === false ? 500 : 0);
  },

  SET_CLIENT (state, client) {
    Vue.set(state, 'client', client);
  },

  STORE_ENTRIES (state, { name, content }) {
    Vue.set(state, name, content);
  },

  EMPTY_ENTRIES (state, name) {
    Vue.set(state, name, null);
  },
};

const actions = {
  init({ commit }) {
    return new Promise(resolve => {
      const client = createClient(config.contentful);

      commit('SET_CLIENT', client);

      Vue.nextTick(() => {
        resolve();
      });
    });
  },

  getEntry({ dispatch, getters, commit }, id) {
    return new Promise((resolve, reject) => {
      const getEntry = () => {
        commit('SET_LOADING_STATE', true);

        getters.client.getEntry(id)
          .then(response => {
            commit('SET_LOADING_STATE', false);
            resolve(response);
          })
          .catch(error => reject(error));
      };
      
      if (getters.client) getEntry();
      else dispatch('init').then(() => getEntry());
    });
  },

  getEntries({ dispatch, getters, commit }, query) {
    return new Promise((resolve, reject) => {
      const getEntries = () => {
        commit(query.search ? 'SET_LOADING_SEARCH_STATE' : 'SET_LOADING_STATE', true);
        delete query.search;

        getters.client.getEntries(query)
          .then(response => {
            commit('SET_LOADING_STATE', false);
            commit('SET_LOADING_SEARCH_STATE', false);
            resolve(response.items);
          })
          .catch(error => reject(error));
      };

      if (getters.client) getEntries();
      else dispatch('init').then(() => getEntries());
    });
  },

  getCategories({ commit, dispatch }) {
    const query = {
      content_type: 'categories',
      select: 'sys.id,fields',
      order: 'fields.order',
    };

    commit('EMPTY_ENTRIES', 'categories');

    dispatch('getEntries', query)
      .then(response => {
        commit('STORE_ENTRIES', {
          name: 'categories',
          content: response,
        });
      });
  },

  getArticles({ commit, dispatch }, { name, parentCode, query, search, highlighted }) {
    const baseQuery = {
      content_type: 'articles',
      select: 'sys.id,fields',
      'fields.topics.sys.id': name,
      'fields.highlighted': highlighted,
      query,
      search,
    };

    const allArticles = {
      'fields.noAccess[ne]': parentCode,
      'fields.exclusiveAccess[exists]': false,
      ...baseQuery,
    };

    const exclusiveArticles = {
      'fields.exclusiveAccess[match]': parentCode,
      ...baseQuery,
    };

    const promises = [
      dispatch('getEntries', allArticles),
      dispatch('getEntries', exclusiveArticles),
    ];

    commit('EMPTY_ENTRIES', 'articles');

    Promise.all(promises)
      .then(response => {
        commit('STORE_ENTRIES', {
          name: highlighted ? 'highlightedArticles' : 'articles',
          content: _.uniqWith(_.flatten(response), _.isEqual), // eslint-disable-line no-undef
        });
      });
  },

  searchArticles({ dispatch }, payload) {
    if ( !payload ) payload = {};

    const query = {
      name: null,
      search: true,
      ...payload,
    };

    dispatch('getArticles', query);
  },

  getHighlightedArticles({ dispatch }, payload) {
    const query = {
      name: null,
      highlighted: true,
      ...payload,
    };

    dispatch('getArticles', query);
  },

  getTopics({ commit, dispatch }) {
    const query = {
      content_type: 'topics',
      select: 'sys.id,fields',
    };

    commit('EMPTY_ENTRIES', 'topics');

    return new Promise(( resolve ) => {
      dispatch('getEntries', query)
        .then(response => {
          commit('STORE_ENTRIES', {
            name: 'topics',
            content: response,
          });

          resolve(response);
        });
    });
  },

  getArticle({ commit, dispatch }, id) {
    commit('EMPTY_ENTRIES', 'article');

    dispatch('getEntry', id)
      .then(response => {
        commit('STORE_ENTRIES', {
          name: 'article',
          content: response,
        });
      });
  },
};

export default {
  namespaced: true,

  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./client/store/contentful.js